import { useEffect, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { PoReviewPanel } from './PoReviewPanel';
import { formatDeliveryDateInput } from '../../lib/demPoExtract';
import {
  createPoMappingContext,
  prepareExtractOrder,
  type PoMappingContext,
} from '../../lib/demPoMapping';
import type { DemPriceRow } from '../../lib/demPoPricing';
import { extractWaOrderFromMessage } from '../../lib/demWaExtract';
import { previewWaOrderNumber, saveWaOrderToPending } from '../../lib/demWaOrders';
import { elapsedProcessingSeconds } from '../../lib/demProcessingTime';
import type { WaQueueProcessPayload } from '../../lib/demWhatsAppQueue';
import type { DemPoExtractHeader, DemPoReviewRow } from '../../types/dem';

type WaProcessingModalProps = {
  open: boolean;
  onClose: () => void;
  actor: string;
  mode: 'queue' | 'manual';
  initialPayload?: WaQueueProcessPayload | null;
  onSaved: (lines: string[], tone: 'ok' | 'warn') => void;
};

function countUnmappedRows(rows: DemPoReviewRow[]): number {
  return rows.filter((row) => {
    const code = row.sysCode.trim().toUpperCase();
    if (['IGNORE', 'SKIP', 'N/A', 'NONE'].includes(code)) return false;
    return row.finalQty > 0 && !row.sysCode.trim();
  }).length;
}

export function WaProcessingModal({
  open,
  onClose,
  actor,
  mode,
  initialPayload,
  onSaved,
}: WaProcessingModalProps) {
  const mappingCtxRef = useRef<PoMappingContext | null>(null);
  const initialHandledRef = useRef(false);
  /** Queue sessions only — wall-clock from opening process modal. */
  const sessionStartedAtRef = useRef<number | null>(null);

  const [customerId, setCustomerId] = useState('');
  const [message, setMessage] = useState('');
  const [queueIds, setQueueIds] = useState<string[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [extractElapsedSec, setExtractElapsedSec] = useState(0);
  const [mapping, setMapping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mappingCtx, setMappingCtx] = useState<PoMappingContext | null>(null);
  const [header, setHeader] = useState<DemPoExtractHeader | null>(null);
  const [reviewRows, setReviewRows] = useState<DemPoReviewRow[]>([]);
  const [priceRows, setPriceRows] = useState<DemPriceRow[]>([]);

  const reviewReady = Boolean(header && mappingCtx);

  const resetState = () => {
    setCustomerId('');
    setMessage('');
    setQueueIds([]);
    setExtracting(false);
    setExtractElapsedSec(0);
    setMapping(false);
    setSaving(false);
    setError(null);
    setHeader(null);
    setReviewRows([]);
    setPriceRows([]);
    initialHandledRef.current = false;
    sessionStartedAtRef.current = null;
  };

  useEffect(() => {
    mappingCtxRef.current = mappingCtx;
  }, [mappingCtx]);

  useEffect(() => {
    if (!extracting) {
      setExtractElapsedSec(0);
      return;
    }
    const started = Date.now();
    const timer = window.setInterval(() => {
      setExtractElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [extracting]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    // WhatsApp timing: only from queue processing (not manual New WA).
    sessionStartedAtRef.current = mode === 'queue' ? Date.now() : null;
    void createPoMappingContext()
      .then(setMappingCtx)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load mapping data.'));
  }, [open, mode]);

  const runExtraction = async (custId: string, msg: string, ids: string[]) => {
    const ctx = mappingCtxRef.current;
    if (!ctx) {
      setError('Mapping data is still loading. Please try again in a moment.');
      return;
    }

    setExtracting(true);
    setMapping(false);
    setError(null);
    setHeader(null);
    setReviewRows([]);
    setPriceRows([]);

    try {
      const order = await extractWaOrderFromMessage(custId, msg);
      setMapping(true);
      // Include message body so C0123* family groups can fuzzy-match the right outlet.
      const prepared = await prepareExtractOrder(ctx, order, { matchExtra: msg });
      prepared.header.delivery_date = formatDeliveryDateInput(prepared.header.delivery_date);
      setHeader(prepared.header);
      setReviewRows(prepared.reviewRows);
      setPriceRows(prepared.priceRows);
      setQueueIds(ids);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'WhatsApp extraction failed.');
    } finally {
      setExtracting(false);
      setMapping(false);
    }
  };

  useEffect(() => {
    if (!open || !mappingCtx || mode !== 'queue' || !initialPayload || initialHandledRef.current) return;
    initialHandledRef.current = true;
    setCustomerId(initialPayload.customerId);
    setMessage(initialPayload.message);
    setQueueIds(initialPayload.ids);
    void runExtraction(initialPayload.customerId, initialPayload.message, initialPayload.ids);
  }, [open, mappingCtx, mode, initialPayload]);

  const handleManualExtract = () => {
    const custId = customerId.trim();
    const msg = message.trim();
    if (!custId) {
      setError('Customer / WhatsApp group is required.');
      return;
    }
    if (!msg) {
      setError('Paste the WhatsApp message first.');
      return;
    }
    void runExtraction(custId, msg, []);
  };

  const handleConfirmSave = async () => {
    if (!header || !mappingCtx) return;
    const unmapped = countUnmappedRows(reviewRows);
    if (!header.sys_customer_code?.trim()) {
      setError('Map a system customer before saving.');
      return;
    }
    if (unmapped > 0) {
      const ok = window.confirm(`${unmapped} line(s) still need a system SKU. Save anyway?`);
      if (!ok) return;
    }

    setSaving(true);
    setError(null);
    try {
      const processingSeconds =
        mode === 'queue' ? elapsedProcessingSeconds(sessionStartedAtRef.current) : null;
      const result = await saveWaOrderToPending(
        header,
        reviewRows,
        actor,
        queueIds,
        processingSeconds && processingSeconds > 0 ? processingSeconds : null,
      );
      const lines = [`WhatsApp order ${result.order_id} saved to Pending.`];
      if (result.notifications.sent > 0) {
        lines.push(`Pending confirmation emails sent: ${result.notifications.sent}.`);
      }
      if (result.notifications.skippedNoEmail > 0) {
        lines.push(`${result.notifications.skippedNoEmail} skipped (no customer email).`);
      }
      if (result.notifications.failed.length > 0) {
        lines.push(`Email failed: ${result.notifications.failed.map((f) => f.reason).join('; ')}`);
      }
      const tone =
        result.notifications.failed.length > 0 || result.notifications.skippedNoEmail > 0 ? 'warn' : 'ok';
      onSaved(lines, tone);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save WhatsApp order.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (extracting || saving) return;
    onClose();
  };

  const title = reviewReady
    ? `Review WA order — ${previewWaOrderNumber()}`
    : mode === 'manual'
      ? 'New WhatsApp order'
      : 'Processing WhatsApp order';

  return (
    <Modal open={open} onClose={handleClose} title={title} fullScreen={reviewReady}>
      <div className="dem-wa-processing">
        {error ? <p className="alert alert-error dem-wa-processing__error">{error}</p> : null}

        {!reviewReady ? (
          <div className="dem-wa-processing__source">
            {mode === 'manual' ? (
              <label className="dem-wa-processing__field">
                <span className="dem-wa-processing__label">Customer / WhatsApp group</span>
                <input
                  type="text"
                  className="field-input"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  placeholder="e.g. T0462-S18"
                />
              </label>
            ) : (
              <p className="dem-wa-processing__meta">
                <strong>{customerId || '—'}</strong>
              </p>
            )}

            <label className="dem-wa-processing__field">
              <span className="dem-wa-processing__label">WhatsApp message</span>
              <textarea
                className="field-input dem-wa-processing__textarea"
                rows={12}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Paste the customer WhatsApp order message"
                readOnly={extracting || mapping}
              />
            </label>

            {extracting || mapping ? (
              <p className="dem-wa-processing__loading">
                {mapping
                  ? 'Applying SKU mappings and prices…'
                  : `AI is reading the WhatsApp message… (${extractElapsedSec}s)`}
              </p>
            ) : (
              <div className="dem-wa-processing__actions">
                <button type="button" className="btn-primary btn-sm" onClick={handleManualExtract}>
                  Extract with AI
                </button>
              </div>
            )}
          </div>
        ) : (
          <PoReviewPanel
            header={header!}
            rows={reviewRows}
            priceRows={priceRows}
            customers={mappingCtx!.customers}
            itemOptions={mappingCtx!.itemOptions}
            mappingCtx={mappingCtx!}
            saving={saving}
            onHeaderChange={setHeader}
            onRowsChange={setReviewRows}
            onPriceRowsChange={setPriceRows}
            onConfirm={() => void handleConfirmSave()}
          />
        )}
      </div>
    </Modal>
  );
}
